import {
  ASSIGNED_SEATING,
  ASSIGNED_SEATING_MODES,
  enums,
} from "@shared/constants";
import { TicketItemGroupSeatPermissionType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import Konva from "konva";
import tinycolor from "tinycolor2";
import SeatingService from "../services/SeatingService";
import {
  MapPermissionsType,
  PositionType,
  PublicBaseLocationSerializer,
  SeatDataType,
  SeatStatus,
  SeatUsageType,
  SelectGAType,
  SelectSeatType,
  TransformType,
} from "../types/seating-types";

const notifier = NotifierFactory.createNotifier();

export default class SeatFactory {
  public seatingService: SeatingService;

  constructor(seatingService: SeatingService) {
    this.seatingService = seatingService;
  }

  public isItemAssigner(): boolean {
    return (
      this.seatingService.mode === ASSIGNED_SEATING_MODES.EDIT_SEAT_TICKETS.id
    );
  }

  // Handles seat styling that is unique to maps where sales are made
  public isSalesMap(): boolean {
    return (
      this.seatingService.mode ===
        ASSIGNED_SEATING_MODES.EVENT_DETAIL_VIEW.id ||
      this.seatingService.mode ===
        ASSIGNED_SEATING_MODES.EVENT_BOX_OFFICE_VIEW.id ||
      this.seatingService.mode === ASSIGNED_SEATING_MODES.GROUP_SALES.id ||
      this.seatingService.mode ===
        ASSIGNED_SEATING_MODES.MODIFY_SEAT_SELECTION.id
    );
  }

  public isAttendanceMap(): boolean {
    return this.seatingService.mode === ASSIGNED_SEATING_MODES.ATTENDANCE.id;
  }

  public isGroupSalesMap(): boolean {
    return this.seatingService.mode === ASSIGNED_SEATING_MODES.GROUP_SALES.id;
  }

  // Display unique seat colors if the seatingService field is enabled
  // OR if the map is the item assigner
  public displayAdvancedColors(): boolean {
    return this.seatingService.advancedSeatColors || this.isItemAssigner();
  }

  /**
   * Check if seat is in the user's basket.
   * If permission exists then seat cannot be in the basket.
   */
  private inBasket(seatDataId: number): boolean {
    const seatPermissions = this.seatingService.seatingMap[seatDataId];
    const hasPermissions = seatPermissions && seatPermissions.length > 0;

    return (
      !hasPermissions &&
      Boolean(this.seatingService.getSeatInCartPermissions(seatDataId))
    );
  }

  private isExchangeSeat(seatDataId: number): boolean {
    const seatPermissions = this.seatingService.seatingMap[seatDataId];
    return Boolean(
      this.seatingService.getSeatExchangePermissions(seatPermissions).length
    );
  }

  private isSeatActive(seatDataId: number): boolean {
    return !this.isExchangeSeat(seatDataId);
  }

  /**
   * Check if seat is selected for group transfer.
   * (permissions are not removed when selected for transfer)
   */
  private selectedForGroupTransfer(seatDataId: number): boolean {
    return (
      this.isGroupSalesMap() &&
      Boolean(this.seatingService.getSeatInCartPermissions(seatDataId))
    );
  }

  static createSeatId(id: number): string {
    return `seat-${id}`;
  }

  static createLabel = (
    text: string,
    {
      x,
      y,
      width,
      height,
      fill = "#555555",
      name = "identifier-label",
      fontSize = 32,
    }: Konva.TextConfig
  ): Konva.Text => {
    const label = new Konva.Text({
      x,
      y,
      width,
      height,
      text: text?.toUpperCase(),
      fontSize,
      align: "center",
      verticalAlign: "middle",
      fill,
      listening: false,
      name,
    });

    label.x(label.x() - label.getWidth() / 2);
    label.y(label.y() - label.getHeight() / 2);

    return label;
  };

  /**
   * Creates canvas object represents a seat.
   */
  static createSeatObject({
    x,
    y,
    fill,
    selected = false,
    data = {},
    isActive = false,
    radius,
    permissions = [],
  }: Konva.CircleConfig): Konva.Circle {
    return new Konva.Circle({
      x,
      y,
      radius,
      fill,
      selected,
      strokeWidth: 0,
      data,
      name: ASSIGNED_SEATING.SEAT.ID,
      isActive,
      permissions,
    });
  }

  /**
   * Creates a seat section.
   * Used for multiple item types on a single seat object.
   */
  static createSeatWedge({
    angle,
    rotation,
    fill,
    radius,
  }: Konva.WedgeConfig): Konva.Wedge {
    const display = new Konva.Wedge({
      x: 0,
      y: 0,
      rotation,
      angle,
      radius,
      fill,
      strokeWidth: 0,
      name: "wedge",
      shadowForStrokeEnabled: false,
      listening: false, // dont listen for events
    });
    return display;
  }

  /**
   * Creates seat wedges for every seat permission.
   * Adds all wedges to the passed Konva Group
   */
  createWedgesForPermissions(
    permissions: MapPermissionsType[],
    group: Konva.Group
  ): void {
    // angle of wedge for each segment
    const angle = 360 / permissions.length;
    // create a wedge for each permission
    permissions.forEach((permission, index: number) => {
      // get colors for a particular permission
      const { colorFill } =
        this.seatingService.itemTypeMap[permission.seatingItemTypeId] || {};
      // create wedge object
      const wedge = SeatFactory.createSeatWedge({
        angle,
        // TODO: change index to sort order
        rotation: angle * index,
        fill: colorFill,
        radius: group.findOne(".seat").getAttr("radius"),
      });
      // add wedge to seat group
      group.add(wedge);
    });
  }

  /**
   * Creates a spinner object.
   * Used for animations when seat is clicked.
   */
  static createSpinnerDisplay(radius: number): Konva.Arc {
    // use a smaller spinner for smaller seat sizes
    const outerRadius = radius > 2 ? radius + 1.25 : radius + 0.5;
    return new Konva.Arc({
      innerRadius: radius,
      outerRadius,
      angle: 180,
      fill: "green",
      strokeWidth: 0.9,
      name: "spinner",
      listening: false, // dont listen for events
    });
  }

  /**
   * Animates an element to "bubble".
   */
  static bubbleAnimation(el: Konva.Group, cb: () => void): void {
    el.to({
      duration: 0.1,
      scaleX: 1.2,
      scaleY: 1.2,
      onFinish: () => {
        el.to({
          scaleX: 1,
          scaleY: 1,
          easing: Konva.Easings.ElasticEaseOut,
          onFinish: () => cb && cb(),
        });
      },
    });
  }

  static spinnerAnimation(
    spinner: Konva.Arc,
    layer: Konva.Layer
  ): Konva.Animation {
    const animation = new Konva.Animation((frame) => {
      if (spinner && frame) {
        const angleDiff = (frame.timeDiff * 700) / 1000;
        spinner.rotate(angleDiff);
      }
    }, layer);
    return animation;
  }

  /**
   * Returns the most readable label color, based on a given seat color
   */
  getLabelColor(
    seatDataId: number,
    seatPermissions: MapPermissionsType[]
  ): string {
    const hasMultiplePermissions =
      seatPermissions && seatPermissions.length > 1;
    const hasSinglePermission = seatPermissions && seatPermissions.length === 1;

    // default label color is black
    let returnColor = "#000";

    if (!this.isSeatSoldOut(seatDataId, seatPermissions)) {
      // for single permission seats (or when the map doesn't display advanced colors)
      // return either white or #555 based on what is more readable
      if (
        hasSinglePermission ||
        (hasMultiplePermissions && !this.displayAdvancedColors())
      ) {
        const seatColor = this.getSeatColor(false, seatPermissions[0]);
        returnColor = tinycolor
          .mostReadable(seatColor, ["#fff", "#555"])
          .toHexString();
      }
      // for multi-permission seats (map must display advanced colors), return white
      else if (hasMultiplePermissions && this.displayAdvancedColors()) {
        returnColor = "#fff";
      }
    }

    return returnColor;
  }

  /**
   * Returns a Konva.Text object containing custom text and positioning for:
   * Accessible, limited visibility, or resale seats
   */
  createCustomLabel(
    seatData: TicketItemGroupSeatPermissionType,
    seatPermissions: MapPermissionsType[],
    seatSize: number,
    circle: Konva.Circle
  ): Konva.Text {
    const accessibilityLabel = ASSIGNED_SEATING.SEAT.STYLE.ACCESSIBLE.seatLabel;
    const visibilityLabel =
      ASSIGNED_SEATING.SEAT.STYLE.LIMITED_VISIBILITY.seatLabel;
    const resaleLabel = ASSIGNED_SEATING.SEAT.STYLE.RESALE.seatLabel;
    const holdLabel = ASSIGNED_SEATING.SEAT.STYLE.HOLD.seatLabel;
    const getCustomText = (): string => {
      let seatLabel = "";
      // prioritize the resale label over the accessibility and visibility labels
      if (
        seatPermissions?.find(
          (seat) =>
            seat.seatingItemResaleId &&
            seat.seatingItemResaleStatus ===
              enums.RESALE_SUBMISSION_STATUS_MAP.PENDING.id
        ) ||
        this.seatingService.getSeatInCartPermissions(seatData.id)?.resaleId
      ) {
        seatLabel = resaleLabel;
      } else if (seatData.is_accessible && seatData.is_limited_visibility) {
        seatLabel = `${accessibilityLabel}/${visibilityLabel}`;
      } else if (seatData.is_limited_visibility) {
        seatLabel = visibilityLabel;
      } else if (seatData.is_accessible) {
        seatLabel = accessibilityLabel;
      } else if (
        seatPermissions?.find(
          (seat) =>
            // only show hold label if the seat is reserved and the invoice_id is null
            // if seat has an invoice_id, it means the seat is purchased.
            seat?.seatStatus === SeatStatus.RESERVED &&
            !seat.seatPurchaser?.invoice_id
        )
      ) {
        seatLabel = holdLabel;
      }
      return seatLabel;
    };

    const labelText = getCustomText();
    const label = SeatFactory.createLabel(labelText, {
      x: circle.x(),
      y: seatSize > 2 ? circle.y() : circle.y() + circle.height() / 8, // centers the label on very small seat sizes
      width: circle.width() * 1.35,
      height: circle.height(),
      fill: this.getLabelColor(seatData.id, seatPermissions),
      name: "identifier-label",
      fontSize: seatSize,
    });

    return label;
  }

  /**
   * Creates a seat object.
   */
  create(
    seatData: TicketItemGroupSeatPermissionType,
    { x = 0, y = 0 }: PositionType,
    seatSize: number,
    locationData: PublicBaseLocationSerializer,
    segmentTransform: TransformType
  ): {
    isActive: boolean;
    node: Konva.Group | Konva.Circle;
  } {
    const seatPermissions = this.seatingService.seatingMap[seatData.id];
    const hasPermissions = seatPermissions && seatPermissions.length > 0;
    const hasMultiplePermissions =
      seatPermissions && seatPermissions.length > 1;

    // Determine if this is an inactive seat (no permissions and not in basket)
    const isInactiveSeat =
      (this.isSalesMap() || this.isAttendanceMap()) &&
      !hasPermissions &&
      !this.inBasket(seatData.id);

    let colorFill: string;

    // Inactive seats get unavailable color
    if (isInactiveSeat) {
      colorFill = ASSIGNED_SEATING.SEAT.STYLE.UNAVAILABLE.colorFill;
    }
    // returns claimed seat color (item assigner)
    else if (
      this.isSeatSoldOut(seatData.id, seatPermissions) &&
      this.isItemAssigner()
    ) {
      colorFill = ASSIGNED_SEATING.SEAT.STYLE.UNAVAILABLE.colorFill;
    }
    // returns unassigned seat color (item assigner)
    else if (!hasPermissions && this.isItemAssigner()) {
      colorFill = ASSIGNED_SEATING.SEAT.STYLE.UNASSIGNED.colorFill;
    }
    // returns available seat color
    else {
      colorFill = this.getSeatColor(
        Boolean(
          this.inBasket(seatData.id) ||
            this.selectedForGroupTransfer(seatData.id) ||
            this.isExchangeSeat(seatData.id)
        ), // if true, returns SEAT.STYLE.CHOSEN color
        seatPermissions?.[0],
        seatData.id // used to determine if the seat should use a custom color, eg. exchange seat
      );
    }

    // Create display group for ALL seats (including inactive)
    // This ensures consistent structure for real-time updates
    const seatDisplay = new Konva.Group({
      x,
      y,
      name: "display",
      id: SeatFactory.createSeatId(seatData.id),
      locationData,
    });

    // create seat base object with data for ALL seats
    const circle = SeatFactory.createSeatObject({
      x: 0,
      y: 0,
      fill: colorFill,
      selected: this.isItemAssigner()
        ? false
        : Boolean(
            this.inBasket(seatData.id) ||
              this.selectedForGroupTransfer(seatData.id)
          ),
      data: seatData, // Include data for ALL seats so they can be identified
      isActive: isInactiveSeat ? false : this.isSeatActive(seatData.id),
      radius: seatSize,
    });
    // add seat base object to group
    seatDisplay.add(circle);

    const highlightCircle = SeatFactory.createHighlightDisplay(seatSize);
    // add highlight behind seat
    seatDisplay.add(highlightCircle);
    highlightCircle.zIndex(0);

    // Add checkmark to ALL seats (including inactive) for consistent structure
    // This allows real-time released seats to show selection checkmark
    const checkmark = SeatFactory.createCheckmarkDisplay(seatSize);
    checkmark.opacity(0); // Hidden by default
    seatDisplay.add(checkmark);

    // Return early for inactive seats - they now have the same structure as active seats
    if (isInactiveSeat) {
      return {
        isActive: false,
        node: seatDisplay,
      };
    }

    // if the seat has more than one permission, creates wedges for advanced display
    if (
      !this.isSeatSoldOut(seatData.id, seatPermissions) &&
      !this.inBasket(seatData.id) &&
      !this.selectedForGroupTransfer(seatData.id) &&
      this.displayAdvancedColors() &&
      hasMultiplePermissions
    ) {
      // sort permissions by item type order
      const sortedPermissions = seatPermissions.sort(
        this.seatingService.sortItemTypeMap
      );
      this.createWedgesForPermissions(sortedPermissions, seatDisplay);
    }

    // show numeric labels on each seat
    if (this.isItemAssigner()) {
      const label = SeatFactory.createLabel(seatData.identifier, {
        x: circle.x(),
        y: circle.y(),
        width: circle.width() * 1.35,
        height: circle.height(),
        fill: this.getLabelColor(seatData.id, seatPermissions),
        fontSize: seatSize * 1.35,
      });
      seatDisplay.add(label);
    }
    // show custom labels on resale/accessible/limited visibility seats
    else if (
      !this.isItemAssigner() &&
      (!this.isSeatSoldOut(seatData.id, seatPermissions) ||
        // if attendance map, show hold label if the seat is reserved even if ticket type is sold out
        (this.isAttendanceMap() &&
          seatPermissions?.find(
            (seat) => seat?.seatStatus === SeatStatus.RESERVED
          ))) &&
      (seatData.is_accessible ||
        seatData.is_limited_visibility ||
        seatPermissions?.find(
          (seat) =>
            seat.seatingItemResaleId || seat?.seatStatus === SeatStatus.RESERVED
        ) ||
        this.seatingService.getSeatInCartPermissions(seatData.id)?.resaleId)
    ) {
      const label = this.createCustomLabel(
        seatData,
        seatPermissions,
        seatSize,
        circle
      );
      seatDisplay.add(label);
      label.opacity(hasPermissions ? 1 : 0);
    }

    const exchangeArrows = SeatFactory.createExchangeDisplay(seatSize);
    exchangeArrows.opacity(this.isExchangeSeat(seatData.id) ? 1 : 0);

    // Update checkmark opacity for active seats (checkmark was already added above)
    checkmark.opacity(
      this.inBasket(seatData.id) || this.selectedForGroupTransfer(seatData.id)
        ? 1
        : 0
    );

    seatDisplay.add(exchangeArrows);

    // get the seatRotation value
    const seatRotation: number =
      360 - segmentTransform.rotation - locationData.transform.rotation;

    // rotate the seat at the end
    // ensures that labels and checkmarks always face up
    seatDisplay.rotate(seatRotation);

    // return seat object
    return {
      isActive: true,
      node: seatDisplay,
    };
  }

  /**
   * Handle click event on a seat node.
   */
  async handleSeatClick(
    node: Konva.Node,
    layer: Konva.Layer,
    pixelRatio: number
  ): Promise<void> {
    if (!this.seatingService.processing) {
      const display = node.findAncestor(".display") as Konva.Group;
      const select = this.isAttendanceMap() ? false : !node.getAttr("selected"); // dont set selected on attendance map
      const cb = SeatingService.getSelectCallBack();

      // disabled seat selection if it is disabled in the SeatingService
      if (!select || !this.seatingService.isSeatSelectionDisabled()) {
        this.setSeatSelected(display, select, layer, {
          animate: true,
          pixelRatio,
          seatNode: node,
          cb,
        });
      } else {
        // if seat selection is disabled, notify the user
        notifier.error("Maximum seat quantity selected.");
      }
    }
  }

  /**
   * Handle selection of multiple seats
   */
  async handleSeatSelect(
    nodes: Konva.Node[],
    layer: Konva.Layer
  ): Promise<void> {
    const seats: SeatDataType[] = [];
    if (!this.seatingService.processing) {
      // Prepare data for callback
      nodes.forEach(async (node) => {
        const display = node.findAncestor(".display") as Konva.Container;
        const seat = display.findOne(".seat");

        if (this.isItemAssigner()) {
          seat.setAttr("selected", true);
        }

        const $data = {
          $permissions: this.seatingService.seatingMap[seat.attrs.data.id],
          node,
          $data: seat.attrs.data,
          $name: seat.getAttr("name"),
          locationData: display.attrs.locationData,
        };
        seats.push($data);
      });

      // Wait for user to respond with permission selection
      const selectPermissionCallback =
        SeatingService.getSeatsSelectedCallBack();
      const data = await selectPermissionCallback(seats);

      if (data && data.length) {
        // Update UI
        data.forEach((seatData: SeatDataType) => {
          const display = seatData.node.findAncestor(".display");

          const location = display.findAncestor(".location");
          location.clearCache();
          // Clear highlight
          const highlight = (display as Konva.Container).findOne(".highlight");
          highlight.opacity(0);
        });
        layer.batchDraw();
      }
    }
  }

  async setSeatSelected(
    display: Konva.Group,
    select: boolean,
    layer: Konva.Layer,
    {
      animate = false,
      pixelRatio = 2,
      seatNode = undefined,
      cb = undefined,
      hideCheckmark = false,
    }: {
      animate: boolean;
      pixelRatio: number;
      seatNode?: Konva.Node;
      cb?: (
        obj: SelectGAType | SelectSeatType
      ) => Promise<void | null> | null | void;
      hideCheckmark?: boolean;
    }
  ): Promise<void> {
    // get Konva nodes needed for animations
    const seat = seatNode || display.findOne(".seat");
    const location = display.findAncestor(".location");
    const checkmark = display.findOne(".checkmark");
    const highlight = display.findOne(".highlight");
    const label = display.findOne(".identifier-label");

    // seat data and it's location data
    const $data = {
      ...seat.attrs.data,
      locationData: display.attrs.locationData,
      node: seatNode,
    };
    let $permissions = this.seatingService.seatingMap[$data.id];

    /**
     * All seats that are clickable should have permissions (unless in item assigner).
     * If no permission exists, then seat was in user basket. Seats in
     * user basket are "blocked" on the db side. This means we need to fetch
     * the permissions for the particular seat.
     */
    if (this.isSalesMap() && !$permissions) {
      const inputSeat = { $data };

      // permissions do not exist so try to fetch them and set seatingMap with the returned values
      $permissions = await this.seatingService.resetSeatPermissions(inputSeat);
    }

    if (cb) {
      // vars needed for animation seat spinner
      // add spinner to group and place it as first in the group
      // so that it is behind the main circle
      const seatRadius = Number(seat.getAttr("radius"));
      const spinner = SeatFactory.createSpinnerDisplay(seatRadius);
      const animation = SeatFactory.spinnerAnimation(spinner, layer);

      // create animation function to pass to caller
      // allows the calling function to animate spinner when needed only
      const animateSpinner = () => {
        // only add spinner to the DOM during the animation
        display.add(spinner);
        spinner.zIndex(0);
        location.clearCache();
        animation.start();
      };

      try {
        const response = await cb({
          $data,
          $permissions,
          $name: seat.name(),
          animateSpinner,
        });

        // stop seat animations
        animation.stop();
        spinner.destroy();

        // return early and do not redraw seats if:
        // 1. the response is null (example: seat selection modal is cancelled)
        if (response === null) {
          return;
        }
      } catch (e) {
        // stop seat animations
        animation.stop();
        spinner.destroy();

        location?.cache({
          pixelRatio,
        });
        location?.setAttr("cachedRatio", pixelRatio);
        layer.batchDraw();

        // return early and do not redraw seats
        return;
      }
    } else {
      // clear location cache for animations and node changes.
      location?.clearCache();
    }

    const hasMultiplePermissions = $permissions && $permissions.length > 1;
    const hasPermissions = $permissions && $permissions.length > 0;

    // redraw seat wedges ONLY IF the callback function had a valid response
    // else the code returned early because a selection modal was cancelled or an error occurred
    if (
      this.isSalesMap() &&
      hasMultiplePermissions &&
      this.displayAdvancedColors()
    ) {
      this.createWedgesForPermissions($permissions, display);
      if (label) {
        label.moveToTop();
      }
    }

    if (select) {
      if (this.isSalesMap() && hasMultiplePermissions) {
        seat.moveToTop();
      }
      seat.setAttr("selected", true);
    } else {
      if (this.isSalesMap() && hasMultiplePermissions) {
        seat.moveToBottom();
      }
      seat.setAttr("selected", false);
    }

    if (this.isItemAssigner()) {
      highlight.opacity(select ? 1 : 0);
    } else {
      // handle seat color
      const colorFill = this.getSeatColor(select, $permissions?.[0]);
      seat.setAttr("fill", colorFill);
      // handle checkmark display
      checkmark.opacity(select && !hideCheckmark ? 1 : 0);
      checkmark.moveToTop();
      // handle accessibility/visibility label display
      if (label) {
        const hideLabel = select || !hasPermissions;
        label.setAttr("fill", this.getLabelColor($data.id, $permissions));
        label.opacity(hideLabel ? 0 : 1);
        label.moveToTop();
      }
      // de-activate the seat on sales maps if it has no permissions
      if (!hasPermissions) {
        seat.setAttr("isActive", false);
      }
    }

    if (this.isItemAssigner()) {
      location.clearCache();
      layer.batchDraw();
    } else if (animate) {
      SeatFactory.bubbleAnimation(display, () => {
        location.cache({
          pixelRatio,
        });
        location.setAttr("cachedRatio", pixelRatio);
      });
    } else {
      location.cache({
        pixelRatio,
      });
      location.setAttr("cachedRatio", pixelRatio);
      layer.batchDraw();
    }
  }

  static createHighlightDisplay(radius: number): Konva.Circle {
    const numberRadius = Number(radius);
    // use a smaller highlight for smaller seat sizes
    const highlightRadius =
      numberRadius > 2
        ? numberRadius +
          1 +
          ASSIGNED_SEATING.EDITOR.SEAT.STYLE.selectedStrokeWidth
        : numberRadius + 0.5;

    return new Konva.Circle({
      radius: highlightRadius,
      fill: "red",
      perfectDrawEnabled: false,
      hitStrokeWidth: 0,
      listening: false,
      opacity: 0,
      transformsEnabled: "position",
      name: "highlight",
    });
  }

  getSeatColor(
    selected: boolean,
    permissions: MapPermissionsType,
    seatDataId?: number
  ): string {
    const showSelectedColor = selected && !this.isItemAssigner();
    const showAdvancedColor =
      this.isItemAssigner() || (!selected && this.displayAdvancedColors());

    let customColor;

    if (seatDataId && this.isExchangeSeat(seatDataId)) {
      customColor = ASSIGNED_SEATING.SEAT.STYLE.EXCHANGE.colorFill;
    }
    const showCustomColor = customColor && !this.isItemAssigner();

    // default: green available seat color
    let returnFill = ASSIGNED_SEATING.SEAT.STYLE.AVAILABLE.colorFill;

    // return yellow seat selection color
    if (showSelectedColor) {
      returnFill = ASSIGNED_SEATING.SEAT.STYLE.CHOSEN.colorFill;
    }
    if (showCustomColor) {
      returnFill = customColor;
    }
    // return advanced seat color
    else if (permissions && showAdvancedColor) {
      returnFill =
        this.seatingService.itemTypeMap[permissions.seatingItemTypeId]
          .colorFill;
    }
    // return inactive seat color
    else if (!showSelectedColor && !permissions) {
      returnFill = ASSIGNED_SEATING.SEAT.STYLE.UNAVAILABLE.colorFill;
    }
    return returnFill;
  }

  // Checkmark icon on seat to symbolize it has been chosen
  static createCheckmarkDisplay(seatSize: number): Konva.Shape {
    // Default checkmark rotation is 0
    const phi = 0;
    const x = -(seatSize / 1.5);
    const y = -(seatSize / 2);

    return new Konva.Shape({
      x: x * Math.cos(phi) - y * Math.sin(phi),
      y: x * Math.sin(phi) + y * Math.cos(phi),
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        // draw checkmark shape
        context.beginPath();
        context.moveTo(0, seatSize / 1.414);
        context.lineTo(seatSize / 4.329, seatSize / 2.121);
        context.lineTo(seatSize / 2.165, seatSize / 1.443);
        context.lineTo(seatSize / 0.862, 0);
        context.lineTo(seatSize / 0.717, seatSize / 4.243);
        context.lineTo(seatSize / 2.165, seatSize / 0.8485);
        context.closePath();

        // (!) Konva specific method, it is very important
        context.fillStrokeShape(shape);
      },
      fill: ASSIGNED_SEATING.SEAT.STYLE.CHOSEN.colorStroke,
      name: "checkmark",
      opacity: 0,
      listening: false,
    });
  }

  // Exchange icon on seat to symbolize it can be exchanged
  static createExchangeDisplay(seatSize: number): Konva.Shape {
    const arrowThickness = seatSize / 5;
    const arrowLength = seatSize;
    const arrowHeadSize = seatSize / 4;
    const verticalOffset = -seatSize / 5; // Adjust this value to control the upward shift

    return new Konva.Shape({
      sceneFunc: (context: Konva.Context, shape: Konva.Shape) => {
        // Right arrow
        context.translate(0, verticalOffset);
        context.beginPath();
        // Draw the main line of the right arrow
        context.moveTo(-arrowLength / 2, 0);
        context.lineTo(arrowLength / 2 - arrowHeadSize, 0);

        // Draw the right arrowhead
        context.moveTo(arrowLength / 2 - arrowHeadSize, -arrowHeadSize / 2);
        context.lineTo(arrowLength / 2, 0);
        context.lineTo(arrowLength / 2 - arrowHeadSize, arrowHeadSize / 2);
        context.closePath();

        // Left arrow
        // Draw the main line of the left arrow
        context.moveTo(arrowLength / 2, seatSize / 2);
        context.lineTo(-arrowLength / 2 + arrowHeadSize, seatSize / 2);

        // Draw the left arrowhead
        context.moveTo(
          -arrowLength / 2 + arrowHeadSize,
          seatSize / 2 - arrowHeadSize / 2
        );
        context.lineTo(-arrowLength / 2, seatSize / 2);
        context.lineTo(
          -arrowLength / 2 + arrowHeadSize,
          seatSize / 2 + arrowHeadSize / 2
        );
        context.closePath();

        // Finalize the shape
        context.fillStrokeShape(shape);
      },
      stroke: ASSIGNED_SEATING.SEAT.STYLE.EXCHANGE.colorStroke,
      strokeWidth: arrowThickness,
      name: "exchange",
      opacity: 1,
      listening: false,
    });
  }

  isSeatSoldOut(seatId: number, permissions: MapPermissionsType[]): boolean {
    const seatOccupied =
      this.seatingService.seatUsages &&
      this.seatingService.seatUsages.length &&
      this.seatingService.seatUsages.some(
        (usage: SeatUsageType) => usage.seat === seatId
      );
    const itemArray =
      permissions && permissions.length
        ? permissions.map(
            (p) => this.seatingService.itemTypeMap[p.seatingItemTypeId]
          )
        : [];
    const allPermissionsSoldOut =
      itemArray && itemArray.length && itemArray.every((t) => t?.sold_out);

    return (
      !this.seatingService.getSeatInCartPermissions(seatId) &&
      Boolean(seatOccupied || allPermissionsSoldOut)
    );
  }

  updateSeatColor(
    display: Konva.Group,
    seatNode: Konva.Node,
    permissions: MapPermissionsType[]
  ): void {
    const isSelected = seatNode.getAttr("selected");
    const label = display.findOne(".identifier-label");

    const hasSinglePermission = permissions && permissions.length === 1;
    const hasMultiplePermissions = permissions && permissions.length > 1;

    // remove seat wedges if they exist
    const oldWedges = display.find(".wedge");
    for (let i = 0; i < oldWedges.length; i += 1) {
      oldWedges[i].destroy();
    }

    // unassigned seat styling is the default
    let seatColor = ASSIGNED_SEATING.SEAT.STYLE.UNASSIGNED.colorFill;
    let labelColor = "#000";

    // seat is selected (yellow checkmark styling)
    if (!this.isItemAssigner() && isSelected) {
      seatColor = ASSIGNED_SEATING.SEAT.STYLE.CHOSEN.colorFill;
    }
    // seat is sold out
    else if (this.isSeatSoldOut(seatNode.attrs.data.id, permissions)) {
      seatColor = ASSIGNED_SEATING.SEAT.STYLE.UNAVAILABLE.colorFill;
    }
    // seat has one permission
    // OR seat has multiple permissions and advanced colors are disabled
    else if (
      hasSinglePermission ||
      (hasMultiplePermissions && !this.displayAdvancedColors())
    ) {
      seatColor = this.getSeatColor(isSelected, permissions[0]);
    }
    // seat has multiple permissions and advanced colors are enabled
    else if (hasMultiplePermissions && this.displayAdvancedColors()) {
      this.createWedgesForPermissions(permissions, display);
    }

    // update the seat and label colors
    seatNode.setAttr("fill", seatColor);
    if (label) {
      // bring the label to the front of the group
      label.moveToTop();
      labelColor = this.getLabelColor(seatNode.attrs.data.id, permissions);
      label.setAttr("fill", labelColor);
    }
  }
}
